// Angular
import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Libraries
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';

// Services
import { DailyReportService } from '../../modules/job/components/daily-report/services/daily-report.service';
import { CoreService } from '../../shared/services';
import { SpinnerService } from '../../shared/components/spinner/services/spinner.service';

// Models
import { Job } from '../../modules/job/models';
import { AppCleanup } from '../../shared/models';

// Helpers
import { FormHelper } from '../../shared/helpers';

// Enums
import { StatusType } from '../../shared/enums';

@Component({
	selector: 'email-jdr-dialog',
	templateUrl: './email-jdr-dialog.component.html',
	styleUrls: ['./email-jdr-dialog.component.scss'],
})
export class EmailJdrDialogComponent extends AppCleanup implements OnInit, OnDestroy {
	componentTitle: string = 'Email JDR';
	job: Job;
	form: FormGroup;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: { job: Job },
		private dialogRef: MatDialogRef<EmailJdrDialogComponent>,
		private formBuilder: FormBuilder,
		private dailyReportService: DailyReportService,
		private spinnerService: SpinnerService,
		private coreService: CoreService,
		private translateService: TranslateService
	) {
		super();
	}

	ngOnInit() {
		this.job = this.data.job;
		this.form = this.formBuilder.group({
			email: [null, [Validators.required, Validators.email]],
			message: [null],
		});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	controlHasError(controlName: string, validationType: string): boolean {
		return FormHelper.controlHasError(this.form, controlName, validationType);
	}

	submit() {
		if (!this.form.dirty) return;

		const controls = this.form.controls;

		if (this.form.invalid) {
			Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
			return;
		}

		this.form.markAsPristine();
		this.form.markAsUntouched();
		this.form.updateValueAndValidity();

		let emailJdrModel = new EmailJdrModel();
		emailJdrModel.email = controls.email.value;
		emailJdrModel.subject = `${this.job.nameDisplay} | ${this.job.locationDisplay}`;
		emailJdrModel.message = controls.message.value;

		this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		this.dailyReportService
			.emailDailyReport(this.job.key, emailJdrModel)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: () => {
					this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					this.dialogRef.close();
					const message = this.translateService.instant('MODULE.JDR.EMAILED');
					this.coreService.toast({ message: message, type: StatusType.SUCCESS });
				},
				error: () => {
					this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					const message = this.translateService.instant('MODULE.JDR.NOT_EMAILED');
					this.coreService.toast({ message: message, type: StatusType.DANGER });
				},
			});
	}
}

export class EmailJdrModel {
	email: string;
	subject: string;
	message: string;
}
